import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { Notices } from "./components/Notices";
import { useAuth } from "./lib/auth";
import { ensureDevice } from "./lib/capture";
import { closeCheckout } from "./lib/checkout";
import { nudgeEvents, onEvent, startEvents, stopEvents } from "./lib/events";
import { syncBundles } from "./lib/installer";
import { markRead, notificationPermission, refreshUnread, setUnread } from "./lib/notify";
import { isNative } from "./lib/platform";
import { load, save } from "./lib/storage";
import { AppHost } from "./pages/AppHost";
import { Login, Register } from "./pages/Auth";
import { People } from "./pages/People";
import { Space } from "./pages/Space";
import { Landing } from "./pages/Landing";
import { AppDetail, Marketplace } from "./pages/Marketplace";
import { Notifications } from "./pages/Notifications";
import { OmniPlans } from "./pages/OmniPlans";
import { Profile, PublicProfile } from "./pages/Profile";
import { ServerSettings, Settings } from "./pages/Settings";

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="splash" aria-busy />;
  if (!user && loc.pathname === "/") return <Landing />; // the front page for visitors
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="splash" aria-busy />;
  return user ? <Navigate to="/" replace /> : <>{children}</>;
}

/**
 * Android hardware back button + deep links (omni://… or app links). A phone notification
 * opens omni://open/<route>#n=<id> (ADR-0031): go there and mark it read.
 */
function useNativeIntegration() {
  const nav = useNavigate();
  useEffect(() => {
    if (!isNative) return;
    let cleanup = () => {};
    const go = (url: string) => {
      const u = new URL(url);
      const nid = /^#n=(\d+)$/.exec(u.hash)?.[1];
      if (nid) void markRead(Number(nid)).catch(() => undefined);
      if (u.searchParams.has("checkout")) void closeCheckout(); // back from paying (ADR-0033)
      nav(u.pathname + u.search);
    };
    import("@capacitor/app").then(({ App: CapApp }) => {
      const back = CapApp.addListener("backButton", ({ canGoBack }) => (canGoBack ? nav(-1) : CapApp.exitApp()));
      const open = CapApp.addListener("appUrlOpen", ({ url }) => go(url));
      void CapApp.getLaunchUrl().then((l) => l?.url && go(l.url)).catch(() => undefined);
      cleanup = () => {
        back.then((h) => h.remove());
        open.then((h) => h.remove());
      };
    });
    return () => cleanup();
  }, [nav]);
}

/**
 * While signed in: keep the live event connection (ADR-0025) and keep installed
 * apps on this device up to date (ADR-0024): at start, when an app is installed
 * or removed anywhere, and when the app comes back to the foreground.
 */
function useDeviceSync() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) {
      stopEvents();
      setUnread(0);
      return;
    }
    const sync = () => void syncBundles().catch(() => undefined);
    const unread = () => void refreshUnread().catch(() => undefined);
    startEvents();
    sync();
    unread();
    if (isNative) {
      // ADR-0031: the phone fetches notifications with Omni closed; ask once to show them
      void ensureDevice().catch(() => undefined);
      if (!load<boolean>("omni.notify.asked", false)) {
        save("omni.notify.asked", true);
        void notificationPermission(true).catch(() => undefined);
      }
    }
    let lastSync = Date.now();
    const off = onEvent((e) => {
      if (e.type === "omni.app.installed" || e.type === "omni.app.uninstalled") sync();
    });
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      nudgeEvents();
      unread();
      if (Date.now() - lastSync > 10 * 60_000) {
        lastSync = Date.now();
        sync();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      off();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.id]);
}

export function App() {
  useNativeIntegration();
  useDeviceSync();
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
    <Notices />
    <Routes>
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/join/:code" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/u/:handle" element={<PublicProfile />} />
      <Route path="/settings/server" element={<ServerSettings />} />
      <Route path="/apps/:appId/*" element={<RequireAuth><AppHost /></RequireAuth>} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<Space />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/omni" element={<OmniPlans />} />
        <Route path="/marketplace/:appId" element={<AppDetail />} />
        <Route path="/people" element={<People />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/invites" element={<Navigate to="/people" replace />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Navigate to="/settings/appearance" replace />} />
        <Route path="/settings/:section" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}
